'use client'

import type * as React from 'react'
import { useState } from 'react'
import type { NumberField } from '@base-ui/react/number-field'
import { cn } from '../lib/utils'
import {
  Field,
  FieldLabel,
  FieldControl,
  FieldDescription,
  FieldError,
} from './field'
import { Input, type InputProps } from './input'
import { NumberInput } from './number-input'
import { EyeIcon, EyeOffIcon } from './icon'
import { IconButton } from './icon-button'

type InputFieldProps = InputProps & {
  label?: React.ReactNode
  /** Description text; becomes the error message when `state="error"`. */
  tip?: React.ReactNode
  /** Manual validity. Omit when a Base UI `Form` drives validation instead. */
  state?: 'error' | 'success'
  /** Fired with the numeric value when `type="number"` renders the auto stepper (see `leading`/`trailing`). */
  onValueChange?: NumberField.Root.Props['onValueChange']
}

function InputField({
  label,
  tip,
  state,
  required,
  disabled,
  className,
  type,
  leading,
  trailing,
  filled,
  minimal,
  onValueChange,
  ...props
}: InputFieldProps) {
  const [revealed, setRevealed] = useState(false)

  const isPassword = type === 'password'
  // `trailing === null` is the explicit opt-out; `undefined` means "not passed" (auto reveal kicks in)
  const autoReveal = isPassword && trailing === undefined
  // Explicit leading/trailing (including the `null` opt-out) falls back to a plain number input.
  const autoStepper =
    type === 'number' && leading === undefined && trailing === undefined
  const resolvedType = autoReveal ? (revealed ? 'text' : 'password') : type
  const resolvedTrailing = autoReveal ? (
    <IconButton
      size="sm"
      semanticRole={state === 'error' ? 'error' : undefined}
      onClick={() => {
        setRevealed((v) => !v)
      }}
    >
      {revealed ? <EyeOffIcon /> : <EyeIcon />}
      {revealed ? 'Hide password' : 'Show password'}
    </IconButton>
  ) : (
    // null opt-out must not render an empty trailing slot
    (trailing ?? undefined)
  )

  // Leading icons inherit the validity color (red lock on an invalid password, etc.) like unni;
  // filled surfaces already recolor everything via the container's text color.
  const resolvedLeading =
    leading && state && !filled ? (
      <span
        className={cn(
          'flex items-center',
          state === 'error' ? 'text-error' : 'text-success',
        )}
      >
        {leading}
      </span>
    ) : (
      leading
    )

  const controlClassName = cn(
    state === 'error' &&
      (filled
        ? 'bg-error text-on-error'
        : minimal
          ? 'text-error'
          : 'border-error has-focus-visible:border-error'),
    state === 'success' &&
      (filled
        ? 'bg-success text-on-success'
        : 'border-success has-focus-visible:border-success'),
    className,
  )

  // InputProps carries HTML-attr string|number types; NumberField wants numbers.
  const toNumber = (v: string | number | readonly string[] | undefined) =>
    // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no value"
    v == null || v === '' ? undefined : Number(v)

  // NumberField.Root reads the Field context itself — no FieldControl in the stepper branch.
  const control = autoStepper ? (
    <NumberInput
      id={props.id}
      name={props.name}
      placeholder={props.placeholder}
      required={required}
      disabled={disabled}
      readOnly={props.readOnly}
      filled={filled}
      minimal={minimal}
      min={toNumber(props.min)}
      max={toNumber(props.max)}
      step={toNumber(props.step)}
      value={props.value === '' ? null : toNumber(props.value)}
      defaultValue={toNumber(props.defaultValue)}
      onValueChange={onValueChange}
      className={controlClassName}
    />
  ) : (
    <FieldControl
      render={
        <Input
          {...props}
          filled={filled}
          minimal={minimal}
          type={resolvedType}
          leading={resolvedLeading}
          trailing={resolvedTrailing}
          className={controlClassName}
        />
      }
      required={required}
    />
  )

  return (
    <Field disabled={disabled} invalid={state === 'error'} data-state={state}>
      {label && (
        <FieldLabel
          className={cn(
            // state colors the whole anatomy (label, border, icon, tip) like unni
            state === 'error' && 'text-error',
            state === 'success' && 'text-success',
            disabled && 'opacity-64',
          )}
        >
          {label}
          {required && <span className="text-error">*</span>}
        </FieldLabel>
      )}
      {control}
      {state === 'error' ? (
        <FieldError match>{tip}</FieldError>
      ) : (
        <>
          {/* form-driven mode: renders only when constraint validation fails */}
          <FieldError />
          {tip && (
            <FieldDescription
              className={state === 'success' ? 'text-success' : undefined}
            >
              {tip}
            </FieldDescription>
          )}
        </>
      )}
    </Field>
  )
}

export { InputField, type InputFieldProps }
